import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import IconPicker from 'client/components/core/Sidebar/components/Settings/components/IconPicker'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DelayOption from 'client/components/core/Sidebar/components/Settings/options/DelayOption'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { FontSize } from 'client/components/core/Sidebar/components/Settings/styles'
import TextLineHeight from 'client/components/core/Sidebar/components/Settings/styles/TextLineHeight'
import VerticalSpacing from 'client/components/core/Sidebar/components/Settings/styles/VerticalSpacing'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { bulletListIcons } from 'common/constants/iconList'
import { BulletListInterface } from 'common/types/entities/BulletListInterface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'

export interface BulletListSettingsProps {
  entity: BulletListInterface
}

const NewBulletListSettings = ({ entity }: BulletListSettingsProps) => {
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<BulletListInterface>()
  const { isMobile } = useDeviceModeContext()

  return (
    <React.Fragment>
      <GroupTitle isFirst>typography</GroupTitle>
      <TextFontUpdater<BulletListInterface>
        entity={entity}
        update={updateEntity}
      />
      <FontSize
        update={updateProp('fontSize')}
        mobileUpdate={updateProp('mobileFontSize')}
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
      />
      <TextLineHeight
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
      />
      <VerticalSpacing
        change={updateProp(
          isMobile ? 'mobileVerticalSpacing' : 'verticalSpacing',
        )}
        verticalSpacing={
          isMobile ? entity.mobileVerticalSpacing : entity.verticalSpacing
        }
      />
      <RangeElement
        label="entity_options.icon_size.title"
        value={entity.listItemIconSize}
        onChange={updateProp('listItemIconSize')}
        min={8}
      />
      <IconPicker
        labelText="entity_options.icon_picker.title"
        iconList={bulletListIcons}
        value={entity.listItemClassName}
        update={updateProp('listItemClassName')}
        searchPlaceholder="settings.components.icon_picker.placeholder"
      />
      <GroupTitle>color</GroupTitle>
      <ColorPickerControl
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
        label="bg color"
        isClearable
      />
      <ColorPickerControl
        update={updateProp('listItemIconColor')}
        mobileUpdate={updateProp('mobileListItemIconColor')}
        color={entity.listItemIconColor}
        mobileColor={entity.mobileListItemIconColor}
        label="icon color"
      />

      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <DelayOption update={updateProp('delay')} delay={entity.delay} />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </React.Fragment>
  )
}

export default NewBulletListSettings
